import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronDown,
  Cloud,
  Folder,
  FolderKanban,
  FolderPlus,
  House,
  Image as ImageIcon,
  MessageSquare,
  MessageSquarePlus,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Settings,
  X
} from 'lucide-react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { SiteLogo } from '../ui/SiteLogo';
import { useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { useOpenSettings } from '../../features/settings/SettingsModalContext';
import { classNames } from '../../utils/classNames';
import { Tooltip } from '../ui/Tooltip';
import { RecentActivityList } from '../../features/activity/RecentActivityList';
import { FolderSettingsModal } from '../../features/activity/FolderSettingsModal';
import { fetchFoldersApi } from '../../api/folders.api';
import { fetchMyPackageApi } from '../../api/packages.api';
import {
  getCachedExpandedFolderId,
  getCachedFolders,
  getCachedFoldersOpen,
  getCachedRecentsOpen,
  setCachedExpandedFolderId,
  setCachedFolders,
  setCachedFoldersOpen,
  setCachedRecentsOpen
} from '../../features/activity/sidebarListsCache';

const badgeToneClassName = {
  yellow:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-400/20 dark:text-yellow-300',
  orange:
    'bg-orange-100 text-orange-800 dark:bg-orange-400/20 dark:text-orange-300'
};

const SIDEBAR_WIDTH_CLASS = {
  expanded: 'w-[260px]',
  collapsed: 'w-[56px]'
};

const sidebarWidthTransition =
  'transition-[width,box-shadow,padding] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none';

const labelFadeClass = (visible) =>
  classNames(
    'overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-out motion-reduce:transition-none',
    visible
      ? 'max-w-[220px] opacity-100 delay-100'
      : 'pointer-events-none max-w-0 opacity-0 duration-150 delay-0'
  );

const SidebarNavItem = ({ to, label, icon, end = false, collapsed = false, badge, badgeTone, onNavigate }) => (
  <NavLink
    to={to}
    end={end}
    title={collapsed ? label : undefined}
    onClick={onNavigate}
    className={({ isActive }) =>
      classNames(
        'group/nav flex min-h-11 w-full items-center overflow-hidden rounded-xl text-[15px] font-medium transition duration-200 md:h-9 md:min-h-0',
        collapsed ? 'justify-center px-0' : 'gap-2.5 px-1.5',
        isActive
          ? 'bg-white text-text shadow-[0_1px_2px_rgba(15,23,42,0.06)] dark:bg-accent-soft dark:text-text dark:shadow-none'
          : 'text-muted hover:bg-panel hover:text-text dark:text-muted dark:hover:bg-accent-soft dark:hover:text-text'
      )
    }
  >
    {({ isActive }) => (
      <>
        <span
          className={classNames(
            'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition',
            isActive
              ? 'bg-accent-soft text-text'
              : 'text-muted group-hover/nav:text-text'
          )}
        >
          {icon}
        </span>
        <span
          className={classNames(
            'flex min-w-0 items-center justify-between gap-2',
            !collapsed && 'flex-1',
            labelFadeClass(!collapsed)
          )}
        >
          <span className="min-w-0 truncate">{label}</span>
          {badge ? (
            <span
              className={classNames(
                'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none',
                badgeToneClassName[badgeTone] || badgeToneClassName.yellow
              )}
            >
              {badge}
            </span>
          ) : null}
        </span>
      </>
    )}
  </NavLink>
);

export const AppSidebar = ({
  collapsed = false,
  onToggleCollapsed,
  variant = 'rail',
  onRequestClose
}) => {
  const navigate = useNavigate();
  const openSettings = useOpenSettings();
  const { t, formatNumber, language } = useI18n();
  const user = useAppSelector((state) => state.auth.user);
  const [peekOpen, setPeekOpen] = useState(false);
  const [folders, setFolders] = useState(() => getCachedFolders());
  const [folderModal, setFolderModal] = useState({ open: false, folder: null });
  const [expandedFolderId, setExpandedFolderId] = useState(() => getCachedExpandedFolderId());
  const [foldersOpen, setFoldersOpen] = useState(() => getCachedFoldersOpen());
  const [recentsOpen, setRecentsOpen] = useState(() => Boolean(getCachedRecentsOpen()));
  const [packageLabel, setPackageLabel] = useState('');
  const leaveTimerRef = useRef(null);

  const toggleRecentsOpen = useCallback(() => {
    setRecentsOpen((value) => {
      const next = !value;
      setCachedRecentsOpen(next);
      return next;
    });
  }, []);

  const displayName =
    user?.displayName ||
    user?.name ||
    (user?.mobile ? formatNumber(user.mobile) : t('settings'));

  const initials = String(displayName)
    .trim()
    .slice(0, 2)
    .toUpperCase();

  const isDrawer = variant === 'drawer';
  const isExpanded = isDrawer || !collapsed || peekOpen;
  const closeIfDrawer = () => {
    if (isDrawer) {
      onRequestClose?.();
    }
  };

  const commitFolders = useCallback((next) => {
    setCachedFolders(next);
    setFolders(next);
  }, []);

  const reloadFolders = useCallback(async () => {
    try {
      const next = await fetchFoldersApi();
      commitFolders(next);
    } catch {
      // Keep the cached list visible while the request fails.
    }
  }, [commitFolders]);

  useEffect(() => {
    reloadFolders();
  }, [reloadFolders]);

  useEffect(() => {
    let cancelled = false;
    fetchMyPackageApi()
      .then((current) => {
        if (cancelled) {
          return;
        }
        const hasPackage = current?.hasPackage ?? current?.HasPackage;
        const name = language === 'fa'
          ? current?.packageNameFa || current?.PackageNameFa
          : current?.packageNameEn || current?.PackageNameEn;
        setPackageLabel(hasPackage ? name : t('noPackageLabel'));
      })
      .catch(() => {
        if (!cancelled) {
          setPackageLabel(t('noPackageLabel'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [language, t]);

  useEffect(() => {
    if (!collapsed) {
      setPeekOpen(false);
    }
  }, [collapsed]);

  useEffect(
    () => () => {
      if (leaveTimerRef.current) {
        window.clearTimeout(leaveTimerRef.current);
      }
    },
    []
  );

  const handleMouseEnter = useCallback(() => {
    if (!collapsed || isDrawer) {
      return;
    }
    if (leaveTimerRef.current) {
      window.clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setPeekOpen(true);
  }, [collapsed, isDrawer]);

  const handleMouseLeave = useCallback(() => {
    if (!collapsed || isDrawer) {
      return;
    }
    if (leaveTimerRef.current) {
      window.clearTimeout(leaveTimerRef.current);
    }
    leaveTimerRef.current = window.setTimeout(() => {
      setPeekOpen(false);
      leaveTimerRef.current = null;
    }, 120);
  }, [collapsed, isDrawer]);

  const handleNewConversation = () => {
    navigate('/chat');
    closeIfDrawer();
  };

  const rail = (
    <div
      className={classNames(
        'flex h-full flex-col bg-app dark:bg-panel-strong',
        isDrawer
          ? 'w-full overflow-hidden border-e border-border pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]'
          : classNames(
              'border-e border-border dark:border-border',
              sidebarWidthTransition,
              isExpanded ? SIDEBAR_WIDTH_CLASS.expanded : SIDEBAR_WIDTH_CLASS.collapsed,
              'overflow-x-hidden',
              collapsed && peekOpen && 'absolute inset-y-0 start-0 z-50 shadow-[4px_0_24px_rgba(0,0,0,0.08)]'
            )
      )}
      onMouseEnter={isDrawer ? undefined : handleMouseEnter}
      onMouseLeave={isDrawer ? undefined : handleMouseLeave}
    >
      <div
        className={classNames(
          'flex shrink-0 items-center gap-1',
          isExpanded ? 'h-14 px-2' : 'h-12 justify-center px-[6px]'
        )}
      >
        <button
          type="button"
          onClick={isDrawer ? onRequestClose : onToggleCollapsed}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-muted transition hover:bg-accent-soft hover:text-text md:h-8 md:w-8"
          aria-label={isDrawer ? t('close') : collapsed ? t('expandSidebar') : t('collapseSidebar')}
        >
          {isDrawer ? (
            <X size={18} />
          ) : language === 'fa'
            ? collapsed && !peekOpen
              ? <PanelRightClose size={18} />
              : <PanelRightOpen size={18} />
            : collapsed && !peekOpen
              ? <PanelLeftClose size={18} />
              : <PanelLeftOpen size={18} />}
        </button>
        <Link
          to="/home"
          onClick={closeIfDrawer}
          className={classNames(
            'flex min-w-0 items-center gap-2 rounded-lg px-1 py-1 transition hover:opacity-90',
            !isExpanded && 'pointer-events-none hidden'
          )}
        >
          <SiteLogo size="sm" className="shadow-none" />
          <span className="min-w-0">
            <span className="block truncate text-[15px] font-semibold leading-5 tracking-tight text-text">
              {t('appName')}
            </span>
            <span className="block truncate text-xs leading-4 text-muted">
              {t('sidebarAppSubtitle')}
            </span>
          </span>
        </Link>
        {isExpanded ? (
          <>
            <span className="min-w-0 flex-1" />
            <Tooltip content={t('createNewConversation')} position="bottom" align="end">
              <button
                type="button"
                onClick={handleNewConversation}
                aria-label={t('createNewConversation')}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted transition hover:bg-accent-soft hover:text-text dark:hover:bg-accent-soft dark:hover:text-text"
              >
                <MessageSquarePlus size={18} />
              </button>
            </Tooltip>
          </>
        ) : null}
      </div>

      {!isExpanded ? (
        <div className="flex justify-center px-[6px] pb-2">
          <Tooltip content={t('createNewConversation')} position="bottom">
            <button
              type="button"
              onClick={handleNewConversation}
              aria-label={t('createNewConversation')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted transition hover:bg-accent-soft hover:text-text dark:hover:bg-accent-soft dark:hover:text-text"
            >
              <MessageSquarePlus size={18} />
            </button>
          </Tooltip>
        </div>
      ) : null}

      <div className="mt-1 min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-2">
        <nav className="flex flex-col gap-1">
          <SidebarNavItem to="/home" end label={t('home')} icon={<House size={16} strokeWidth={1.75} />} collapsed={!isExpanded} onNavigate={closeIfDrawer} />
          <SidebarNavItem to="/chat" label={t('chatSection')} icon={<MessageSquare size={16} strokeWidth={1.75} />} collapsed={!isExpanded} onNavigate={closeIfDrawer} />
          <SidebarNavItem to="/projects" label={t('productGeneration')} icon={<FolderKanban size={16} strokeWidth={1.75} />} collapsed={!isExpanded} badge={t('special')} badgeTone="orange" onNavigate={closeIfDrawer} />
          <SidebarNavItem to="/images" label={t('imageSection')} icon={<ImageIcon size={16} strokeWidth={1.75} />} collapsed={!isExpanded} onNavigate={closeIfDrawer} />
          <SidebarNavItem to="/cloud" label={t('homeServiceCloudTitle')} icon={<Cloud size={16} strokeWidth={1.75} />} collapsed={!isExpanded} badge={t('new')} badgeTone="yellow" onNavigate={closeIfDrawer} />
        </nav>

        <div
          className={classNames(
            'grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
            isExpanded ? 'grid-rows-[1fr] opacity-100 delay-75' : 'pointer-events-none grid-rows-[0fr] opacity-0'
          )}
          aria-hidden={!isExpanded}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="mt-5 flex flex-col gap-4 px-1 pb-4">
            <div>
              <div className="mb-1 flex items-center justify-between gap-1 px-1">
                <button
                  type="button"
                  onClick={() =>
                    setFoldersOpen((value) => {
                      const next = !value;
                      setCachedFoldersOpen(next);
                      return next;
                    })
                  }
                  className="flex min-w-0 flex-1 items-center gap-1 text-start text-xs font-semibold uppercase tracking-[0.08em] text-muted transition hover:text-text dark:hover:text-text"
                  aria-expanded={foldersOpen}
                >
                  <ChevronDown
                    size={14}
                    className={classNames('shrink-0 transition-transform duration-200', !foldersOpen && '-rotate-90')}
                  />
                  <span className="truncate">{t('folders')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFolderModal({ open: true, folder: null })}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted hover:opacity-95/70 hover:text-text"
                  aria-label={t('folderNew')}
                >
                  <FolderPlus size={16} />
                </button>
              </div>
              <div
                className={classNames(
                  'grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
                  foldersOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                )}
              >
                <div className="min-h-0 overflow-hidden">
              {folders.length === 0 ? (
                <p className="px-1 text-sm text-muted">{t('folderEmpty')}</p>
              ) : (
                <div className="flex flex-col gap-0">
                  {folders.map((folder) => (
                    <div key={folder.id}>
                      <div className="group flex items-center gap-0.5 rounded-lg px-1.5 py-1 hover:bg-accent-soft dark:hover:bg-accent-soft">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedFolderId((value) => {
                              const next = value === folder.id ? null : folder.id;
                              setCachedExpandedFolderId(next);
                              return next;
                            })
                          }
                          className="flex min-w-0 flex-1 items-center gap-2 text-start text-sm text-text"
                        >
                          <Folder size={16} className="shrink-0 opacity-70" style={folder.color ? { color: folder.color } : undefined} />
                          <span className="truncate">{folder.name}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            navigate(`/chat?folderId=${encodeURIComponent(folder.id)}`);
                            closeIfDrawer();
                          }}
                          className={classNames(
                            'inline-flex h-9 w-9 items-center justify-center rounded-md text-muted transition hover:opacity-95/70 md:h-7 md:w-7',
                            isDrawer ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                          )}
                          aria-label={t('folderNewChat')}
                          title={t('folderNewChat')}
                        >
                          <MessageSquarePlus size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            navigate(`/folders/${folder.id}`);
                            closeIfDrawer();
                          }}
                          className={classNames(
                            'inline-flex h-9 w-9 items-center justify-center rounded-md text-muted transition hover:opacity-95/70 md:h-7 md:w-7',
                            isDrawer ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                          )}
                          aria-label={t('folderOpen')}
                          title={t('folderOpen')}
                        >
                          <Settings size={15} />
                        </button>
                      </div>
                      {expandedFolderId === folder.id ? (
                        <div className="ms-3 border-s border-border ps-2 dark:border-border">
                          <button
                            type="button"
                            onClick={() => {
                              navigate(`/chat?folderId=${encodeURIComponent(folder.id)}`);
                              closeIfDrawer();
                            }}
                            className="mb-1 flex min-h-10 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-start text-sm text-muted hover:bg-accent-soft hover:text-text dark:hover:bg-accent-soft dark:hover:text-text"
                          >
                            <MessageSquarePlus size={14} />
                            {t('folderNewChat')}
                          </button>
                          <RecentActivityList
                            take={100}
                            folders={folders}
                            folderIdFilter={folder.id}
                            excludeFoldered={false}
                            compact
                            onCreateFolder={() => setFolderModal({ open: true, folder: null })}
                            onNavigate={closeIfDrawer}
                          />
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
                </div>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={toggleRecentsOpen}
                className="mb-1 flex w-full items-center gap-1 px-1 text-start text-xs font-semibold uppercase tracking-[0.08em] text-muted transition hover:text-text dark:hover:text-text"
                aria-expanded={recentsOpen}
              >
                <ChevronDown
                  size={14}
                  className={classNames('shrink-0 transition-transform duration-200', !recentsOpen && '-rotate-90')}
                />
                <span className="truncate">{t('recent')}</span>
              </button>
              <div
                className={classNames(
                  'grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
                  recentsOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                )}
              >
                <div className="min-h-0 overflow-hidden">
                  <RecentActivityList
                    take={100}
                    folders={folders}
                    groupByDate={false}
                    onCreateFolder={() => setFolderModal({ open: true, folder: null })}
                    onNavigate={closeIfDrawer}
                  />
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={classNames(
          'mt-auto flex flex-col overflow-hidden border-t border-border',
          sidebarWidthTransition,
          isExpanded ? 'p-3' : 'p-2'
        )}
      >
        <button
          type="button"
          onClick={() => {
            openSettings();
            closeIfDrawer();
          }}
          className="flex min-h-12 w-full items-center gap-2.5 overflow-hidden rounded-lg px-1.5 py-1.5 text-start transition hover:bg-accent-soft"
          aria-label={isExpanded ? undefined : t('settings')}
        >
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent-soft text-xs font-semibold text-text">
            {initials}
          </span>
          <span className={classNames('min-w-0 flex-1', labelFadeClass(isExpanded))}>
            <span className="block truncate text-[15px] font-medium text-text">{displayName}</span>
            <span className="block truncate text-[13px] text-muted">{packageLabel || t('proPlan')}</span>
          </span>
          <span className={classNames('shrink-0 text-muted', labelFadeClass(isExpanded))}>
            <Settings size={16} />
          </span>
        </button>
      </div>

      <FolderSettingsModal
        open={folderModal.open}
        folder={folderModal.folder}
        onClose={() => setFolderModal({ open: false, folder: null })}
        onSaved={() => reloadFolders()}
        onDeleted={() => reloadFolders()}
      />
    </div>
  );

  if (isDrawer) {
    return (
      <aside className="flex h-full min-h-0 w-full flex-col overflow-hidden" aria-label={t('navigation')}>
        {rail}
      </aside>
    );
  }

  return (
    <aside
      className={classNames(
        'relative z-50 hidden h-full shrink-0 md:block',
        sidebarWidthTransition,
        collapsed ? SIDEBAR_WIDTH_CLASS.collapsed : SIDEBAR_WIDTH_CLASS.expanded,
        collapsed && peekOpen ? 'overflow-visible' : 'overflow-hidden'
      )}
    >
      {rail}
    </aside>
  );
};
